import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  Row, Col, Card, Statistic, Progress, Button, Alert, Spin, Typography, Space 
} from 'antd';
import {
  FileTextOutlined,
  QuestionCircleOutlined,
  DatabaseOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  MessageOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';

const { Title, Text, Paragraph } = Typography;

function Dashboard() {
  const { user } = useSelector((state) => state.auth);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [docsRes, usageRes] = await Promise.all([
        api.get('/documents', { params: { page: 1, page_size: 1 } }),
        api.get('/billing/usage')
      ]);
      
      setStats({
        documentCount: docsRes.data.total,
        usage: usageRes.data
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTokenUsagePercent = () => {
    if (!stats?.usage || !stats.usage.monthly_token_limit || stats.usage.monthly_token_limit === 0) return 0;
    const percent = Math.round((stats.usage.tokens_used_this_period / stats.usage.monthly_token_limit) * 100);
    return isNaN(percent) ? 0 : Math.min(percent, 100);
  };

  const getStoragePercent = () => {
    if (!stats?.usage || !stats.usage.storage_limit_gb || stats.usage.storage_limit_gb === 0) return 0;
    const percent = Math.round((stats.usage.storage_used_gb / stats.usage.storage_limit_gb) * 100);
    return isNaN(percent) ? 0 : Math.min(percent, 100);
  };

  const getUsageColor = (percent) => {
    if (percent >= 90) return '#DC2626';
    if (percent >= 80) return '#D97706';
    return '#0B3D91';
  };

  const mockUsageData = [
    { date: 'Mon', tokens: 12000 },
    { date: 'Tue', tokens: 15000 },
    { date: 'Wed', tokens: 18000 },
    { date: 'Thu', tokens: 14000 },
    { date: 'Fri', tokens: 20000 },
    { date: 'Sat', tokens: 8000 },
    { date: 'Sun', tokens: 10000 },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  const tokenPercent = getTokenUsagePercent();
  const storagePercent = getStoragePercent();

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>
          Welcome back{user?.full_name ? `, ${user.full_name}` : ''}
        </Title>
        <Paragraph type="secondary" style={{ fontSize: '16px', marginBottom: 0 }}>
          Manage your industrial documents and get AI-powered insights
        </Paragraph>
      </div>

      {tokenPercent >= 80 && (
        <Alert
          message="Token Usage Alert"
          description={`You have used ${tokenPercent}% of your monthly token quota. ${
            tokenPercent >= 100 
              ? 'Consider upgrading your plan to avoid service interruption.' 
              : 'Consider upgrading to avoid hitting your limit.'
          }`}
          type={tokenPercent >= 100 ? 'error' : 'warning'}
          icon={<ExclamationCircleOutlined />}
          showIcon
          closable
          style={{ marginBottom: '24px' }}
        />
      )}

      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ height: '100%' }}>
            <Statistic
              title="Documents Uploaded"
              value={stats?.documentCount || 0}
              suffix={`/ ${stats?.usage?.document_limit || '∞'}`}
              prefix={<FileTextOutlined style={{ color: '#0B3D91' }} />}
              valueStyle={{ color: '#111827', fontSize: '28px', fontWeight: 600 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ height: '100%' }}>
            <Statistic
              title="Questions Asked"
              value={stats?.usage?.queries_this_period || 0}
              prefix={<QuestionCircleOutlined style={{ color: '#0284C7' }} />}
              valueStyle={{ color: '#111827', fontSize: '28px', fontWeight: 600 }}
            />
            <Text type="secondary" style={{ fontSize: '13px' }}>This billing period</Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ height: '100%' }}>
            <Statistic
              title="Storage Used"
              value={stats?.usage?.storage_used_gb?.toFixed(2) || '0.00'}
              suffix={`GB / ${stats?.usage?.storage_limit_gb || 0}GB`}
              prefix={<DatabaseOutlined style={{ color: '#7C3AED' }} />}
              valueStyle={{ color: '#111827', fontSize: '28px', fontWeight: 600 }}
            />
            <Progress 
              percent={storagePercent} 
              strokeColor={getUsageColor(storagePercent)}
              showInfo={false}
              size="small"
              style={{ marginTop: '8px' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ height: '100%' }}>
            <Statistic
              title="Token Usage"
              value={tokenPercent}
              suffix="%"
              prefix={<ThunderboltOutlined style={{ color: getUsageColor(tokenPercent) }} />}
              valueStyle={{ color: getUsageColor(tokenPercent), fontSize: '28px', fontWeight: 600 }}
            />
            <Progress 
              percent={tokenPercent} 
              strokeColor={getUsageColor(tokenPercent)}
              showInfo={false}
              size="small"
              style={{ marginTop: '8px' }}
            />
            <Text type="secondary" style={{ fontSize: '13px' }}>
              {(stats?.usage?.tokens_used_this_period || 0).toLocaleString()} / {(stats?.usage?.monthly_token_limit || 0).toLocaleString()}
            </Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <Card 
            title="Token Usage Trend" 
            bordered={false}
            extra={<Text type="secondary">Last 7 days</Text>}
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={mockUsageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="date" 
                  stroke="#6B7280"
                  style={{ fontSize: '13px' }}
                />
                <YAxis 
                  stroke="#6B7280"
                  style={{ fontSize: '13px' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="tokens" 
                  stroke="#0B3D91" 
                  strokeWidth={3}
                  dot={{ fill: '#0B3D91', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card 
            title="System Status" 
            bordered={false}
            style={{ height: '100%' }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Document Processing</Text>
                <Text strong style={{ color: '#16A34A' }}>
                  <CheckCircleOutlined /> Operational
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>AI Question Answering</Text>
                <Text strong style={{ color: '#16A34A' }}>
                  <CheckCircleOutlined /> Operational
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Vector Search</Text>
                <Text strong style={{ color: '#16A34A' }}>
                  <CheckCircleOutlined /> Operational
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Billing System</Text>
                <Text strong style={{ color: '#16A34A' }}>
                  <CheckCircleOutlined /> Operational
                </Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card title="Quick Actions" bordered={false}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Link to="/documents">
              <Button 
                type="primary" 
                size="large" 
                block
                icon={<UploadOutlined />}
                style={{ height: '80px', fontSize: '16px' }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>Upload Documents</div>
                  <div style={{ fontSize: '13px', fontWeight: 400, opacity: 0.9 }}>
                    Add technical manuals
                  </div>
                </div>
              </Button>
            </Link>
          </Col>

          <Col xs={24} sm={8}>
            <Link to="/ask">
              <Button 
                size="large" 
                block
                icon={<MessageOutlined />}
                style={{ height: '80px', fontSize: '16px' }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>Ask Questions</div>
                  <div style={{ fontSize: '13px', fontWeight: 400 }}>
                    Get AI-powered answers
                  </div>
                </div>
              </Button>
            </Link>
          </Col>

          <Col xs={24} sm={8}>
            <Link to="/billing">
              <Button 
                size="large" 
                block
                icon={<DollarOutlined />}
                style={{ height: '80px', fontSize: '16px' }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>Manage Billing</div>
                  <div style={{ fontSize: '13px', fontWeight: 400 }}>
                    View usage & subscription
                  </div>
                </div>
              </Button>
            </Link>
          </Col>
        </Row>
      </Card>
    </div>
  );
}

export default Dashboard;
